import {defineStore} from 'pinia'
import {computed, ref, watch} from 'vue'
import type {RouteLocationNormalized} from 'vue-router'
import {useRouter} from 'vue-router'
import {useWorkspaceStore} from '@toolify/client/src/stores/WorkspaceStore/useWorkspaceStore'
import {useLocalPreferenceStore} from '@toolify/client/src/stores/LocalPreferenceStore/useLocalPreferenceStore'
import {LocalPreferenceType} from '@toolify/client/src/stores/LocalPreferenceStore/enum/LocalPreferenceType'
import {IWorkspaceTab} from '@toolify/client/src/modules/workspaceTabs/types/IWorkspaceTab'
import {getTabFromRoute} from '@toolify/client/src/modules/workspaceTabs/functions/getTabFromRoute'
import {RouteName} from '@toolify/client/src/setup/router/enum/RouteName'
import {WorkspaceTabKindType} from '@toolify/client/src/modules/workspaceTabs/enum/WorkspaceTabKindType'

function parseTabs(raw: string | undefined): IWorkspaceTab[] {
  if(!raw) {
    return []
  }
  try {
    const parsed = JSON.parse(raw) as unknown
    if(!Array.isArray(parsed)) {
      return []
    }
    return parsed.filter((item): item is IWorkspaceTab => {
      return Boolean(
        item
        && typeof item === 'object'
        && typeof (item as IWorkspaceTab).key === 'string'
        && typeof (item as IWorkspaceTab).name === 'string'
        && ((item as IWorkspaceTab).kind === WorkspaceTabKindType.Resource
          || (item as IWorkspaceTab).kind === WorkspaceTabKindType.Route)
        && (item as IWorkspaceTab).params
        && typeof (item as IWorkspaceTab).params === 'object',
      )
    })
  } catch {
    return []
  }
}

export const useWorkspaceTabStore = defineStore('workspaceTab', () => {
  const router = useRouter()
  const workspaceStore = useWorkspaceStore()
  const localPreferenceStore = useLocalPreferenceStore()
  const tabs = ref<IWorkspaceTab[]>([])

  const workspaceName = computed(() => workspaceStore.currentWorkspace?.name ?? null)

  function loadTabs() {
    if(!workspaceName.value) {
      tabs.value = []
      return
    }
    tabs.value = parseTabs(localPreferenceStore.get(LocalPreferenceType.WorkspaceTabs, workspaceName.value))
  }

  function persistTabs() {
    if(!workspaceName.value) {
      return
    }
    void localPreferenceStore.set(
      LocalPreferenceType.WorkspaceTabs,
      JSON.stringify(tabs.value),
      workspaceName.value,
    )
  }

  watch(workspaceName, () => {
    loadTabs()
  }, {immediate: true})

  function syncFromRoute(route: RouteLocationNormalized) {
    const tab = getTabFromRoute(route)
    if(!tab) {
      return
    }
    const existingIndex = tabs.value.findIndex(item => item.key === tab.key)
    if(existingIndex === -1) {
      tabs.value = [...tabs.value, tab]
    } else {
      const next = [...tabs.value]
      next[existingIndex] = {
        ...next[existingIndex],
        name: tab.name,
        params: tab.params,
        resourceType: tab.resourceType,
        resourceId: tab.resourceId,
      }
      tabs.value = next
    }
    persistTabs()
  }

  function reorderTab(tabKey: string, index: number) {
    const fromIndex = tabs.value.findIndex(item => item.key === tabKey)
    if(fromIndex === -1 || fromIndex === index) {
      return
    }
    const next = [...tabs.value]
    const [moved] = next.splice(fromIndex, 1)
    const insertAt = fromIndex < index ? index - 1 : index
    next.splice(Math.max(0, insertAt), 0, moved)
    tabs.value = next
    persistTabs()
  }

  async function activateTab(tab: IWorkspaceTab) {
    await router.push({
      name: tab.name,
      params: tab.params,
    })
  }

  async function closeTab(tabKey: string) {
    const index = tabs.value.findIndex(item => item.key === tabKey)
    if(index === -1) {
      return
    }
    const closed = tabs.value[index]
    const wasActive = getTabFromRoute(router.currentRoute.value)?.key === tabKey
    const next = tabs.value.filter(item => item.key !== tabKey)
    tabs.value = next
    persistTabs()
    if(!wasActive) {
      return
    }
    const neighbor = next[index] ?? next[index - 1] ?? null
    if(neighbor) {
      await activateTab(neighbor)
      return
    }
    const name = closed.params.workspaceName
    if(name) {
      await router.push({
        name: RouteName.WorkspaceHome,
        params: {
          workspaceName: name,
        },
      })
    }
  }

  return {
    tabs,
    syncFromRoute,
    reorderTab,
    activateTab,
    closeTab,
    loadTabs,
  }
})
